import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { HubShell } from "../components/hub/shell";
import AddEditCompanyModal from "../components/AddEditCompanyModal";
import CompanyDetailModal from "../components/CompanyDetailModal";
import { useNotification } from "../contexts/NotificationContext";
import { useView } from "../contexts/ViewContext";
import { getAllCompanies, deleteCompany } from "../api";

// fn_admin_list_organizations refuses non-platform-admin callers (the six
// staff_full_access users among them) with "Platform admin required". The
// Organizations admin page must NOT fall back to fn_get_my_organizations for
// them — they get no admin screens — so we detect that exact error and render
// a dedicated "platform admin required" state instead of the org list.
const PLATFORM_ADMIN_REQUIRED = /platform\s+admin\s+required/i;

const CompanyManagementPage = () => {
  const navigate = useNavigate();
  const { showSuccess, showError, showConfirm } = useNotification();
  const username = localStorage.getItem("username") || "admin";
  // Multi-Org Membership Epic 1 — role follows the active org (not localStorage).
  const currentUserRole = useView().role;

  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  // True when fn_admin_list_organizations refused this caller with
  // "Platform admin required" — renders the no-admin-screens state.
  const [requiresPlatformAdmin, setRequiresPlatformAdmin] = useState(false);

  // Filters
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortBy, setSortBy] = useState("name");

  // Modals
  const [showAddCompanyModal, setShowAddCompanyModal] = useState(false);
  const [showEditCompanyModal, setShowEditCompanyModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Check if user has permission
  useEffect(() => {
    if (currentUserRole > 1) {
      navigate("/dashboard");
    }
  }, [currentUserRole, navigate]);

  useEffect(() => {
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    try {
      setLoading(true);
      setError("");
      setRequiresPlatformAdmin(false);
      // requireAdmin: true — the Organizations admin page must NOT fall back to
      // fn_get_my_organizations. fn_admin_list_organizations is the only source
      // here; if it refuses the caller, we surface "platform admin required".
      const response = await getAllCompanies({ requireAdmin: true });
      const companyList = response?.data ?? response ?? [];
      setCompanies(Array.isArray(companyList) ? companyList : []);
    } catch (err) {
      const msg = err?.message || "";
      if (PLATFORM_ADMIN_REQUIRED.test(msg)) {
        // The six full-access staff land here: no admin screens, just the
        // "platform admin required" state.
        setRequiresPlatformAdmin(true);
        setError("");
      } else {
        setRequiresPlatformAdmin(false);
        setError(msg || "Failed to load organizations");
      }
      setCompanies([]);
    } finally {
      setLoading(false);
    }
  };

  // Filtering and sorting
  const filteredCompanies = useMemo(() => {
    const filtered = companies.filter((company) => {
      const searchLower = searchQuery.toLowerCase();
      const matchesSearch =
        !searchQuery ||
        company.name?.toLowerCase().includes(searchLower) ||
        company.description?.toLowerCase().includes(searchLower);

      const matchesStatus =
        statusFilter === "all" || String(company.status) === String(statusFilter);

      return matchesSearch && matchesStatus;
    });

    return filtered.sort((a, b) => {
      if (sortBy === "name") {
        return (a.name || "").localeCompare(b.name || "");
      } else if (sortBy === "users") {
        // Prefer member_count (from fn_get_my_organizations / fn_admin_list_organizations)
        // which is pre-computed by the RPC; fall back to legacy users[] length.
        return (b.member_count ?? b.users?.length ?? 0) - (a.member_count ?? a.users?.length ?? 0);
      } else if (sortBy === "projects") {
        return (b.client_count ?? b.projects?.length ?? 0) - (a.client_count ?? a.projects?.length ?? 0);
      } else if (sortBy === "newest") {
        return new Date(b.createdAt) - new Date(a.createdAt);
      } else if (sortBy === "oldest") {
        return new Date(a.createdAt) - new Date(b.createdAt);
      }
      return 0;
    });
  }, [companies, searchQuery, statusFilter, sortBy]);

  // Pagination
  const totalPages = Math.ceil(filteredCompanies.length / itemsPerPage);
  const paginatedCompanies = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredCompanies.slice(start, start + itemsPerPage);
  }, [filteredCompanies, currentPage, itemsPerPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, statusFilter, sortBy]);

  const handleDeleteCompany = async (company) => {
    if (!company) return;

    const confirmed = await showConfirm({
      title: "Delete Organization",
      message: `Are you sure you want to delete "${company.name}"?\n\nNote: You must remove all users and projects from this company first.`,
      confirmText: "Delete",
      cancelText: "Cancel",
      confirmButtonClass: "bg-gradient-to-r from-red-600 to-rose-600 hover:from-red-700 hover:to-rose-700",
    });

    if (!confirmed) return;

    try {
      await deleteCompany(company.id);
      showSuccess("Company Deleted", `${company.name} has been successfully deleted.`);
      await fetchCompanies();
      setSelectedCompany(null);
    } catch (err) {
      const errorMessage = err?.message || "Failed to delete company";
      showError("Deletion Failed", errorMessage);
    }
  };

  const handleViewDetails = (company) => {
    setSelectedCompany(company);
    setShowDetailModal(true);
  };

  const handleEditCompany = (company) => {
    setSelectedCompany(company);
    setShowEditCompanyModal(true);
  };

  return (
    <HubShell>
      <div className="px-6 md:px-10 py-6 space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-3">
              <div className="p-2 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-lg">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
              Organization Management
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Create organizations and group users & clients under them
            </p>
          </div>
          {!requiresPlatformAdmin && (
            <button
              onClick={() => setShowAddCompanyModal(true)}
              className="px-5 py-2.5 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white rounded-lg font-semibold shadow-lg shadow-emerald-500/25 transition-all flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path d="M12 4v16m8-8H4" />
              </svg>
              Create Organization
            </button>
          )}
        </div>

        {requiresPlatformAdmin ? (
          /* Platform admin required — the six full-access staff (and any other
             non-platform-admin caller) get no admin screens, just this state. */
          <div
            data-testid="platform-admin-required"
            className="bg-white dark:bg-gray-900 rounded-xl shadow border dark:border-gray-800 p-12 text-center"
          >
            <div className="w-16 h-16 bg-amber-100 dark:bg-amber-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-amber-600 dark:text-amber-400" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                <path d="M12 15v2m0 0v3m0-3h6m-6 0H6m6-12a9 9 0 100 18 9 9 0 000-18z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.5M12 9a1.5 1.5 0 100-3 1.5 1.5 0 000 3z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
              Platform admin required
            </h3>
            <p className="text-gray-500 dark:text-gray-400 max-w-md mx-auto">
              The Organizations admin page is restricted to platform admins. You
              can still browse your organizations from the dashboard.
            </p>
          </div>
        ) : (
        <>
        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white dark:bg-gray-900 rounded-xl p-4 shadow border dark:border-gray-800">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg">
                <svg className="w-5 h-5 text-emerald-600 dark:text-emerald-400" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4z" />
                  <path fillRule="evenodd" d="M18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9zM4 13a1 1 0 011-1h1a1 1 0 110 2H5a1 1 0 01-1-1zm5-1a1 1 0 100 2h1a1 1 0 100-2H9z" clipRule="evenodd" />
                </svg>
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{companies.length}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Total Companies</p>
              </div>
            </div>
          </div>
          <div className="bg-white dark:bg-gray-900 rounded-xl p-4 shadow border dark:border-gray-800">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                <svg className="w-5 h-5 text-green-600 dark:text-green-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                  {companies.filter(c => c.status === 1).length}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Active Companies</p>
              </div>
            </div>
          </div>
          <div className="bg-white dark:bg-gray-900 rounded-xl p-4 shadow border dark:border-gray-800">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                <svg className="w-5 h-5 text-blue-600 dark:text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
                </svg>
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                  {companies.reduce((sum, c) => sum + (c.member_count ?? c.users?.length ?? 0), 0)}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Total Users</p>
              </div>
            </div>
          </div>
          <div className="bg-white dark:bg-gray-900 rounded-xl p-4 shadow border dark:border-gray-800">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                <svg className="w-5 h-5 text-purple-600 dark:text-purple-400" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M2 6a2 2 0 012-2h12a2 2 0 012 2v2a2 2 0 100 4v2a2 2 0 01-2 2H4a2 2 0 01-2-2v-2a2 2 0 100-4V6z" />
                </svg>
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                  {companies.reduce((sum, c) => sum + (c.client_count ?? c.projects?.length ?? 0), 0)}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Total Clients</p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white dark:bg-gray-900 rounded-xl shadow border dark:border-gray-800 p-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Search */}
            <div className="md:col-span-2">
              <label className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2 block">
                Search
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search by name or description..."
                  className="w-full border dark:border-gray-700 rounded-lg px-10 py-2.5 focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                />
                <svg
                  className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 dark:text-gray-500"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <circle cx="11" cy="11" r="7" />
                  <path d="M21 21l-4.3-4.3" />
                </svg>
              </div>
            </div>

            {/* Status Filter */}
            <div>
              <label className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2 block">
                Status
              </label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full border dark:border-gray-700 rounded-lg px-3 py-2.5 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-emerald-500"
              >
                <option value="all">All Status</option>
                <option value="1">Active</option>
                <option value="0">Inactive</option>
              </select>
            </div>

            {/* Sort */}
            <div>
              <label className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2 block">
                Sort By
              </label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="w-full border dark:border-gray-700 rounded-lg px-3 py-2.5 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-emerald-500"
              >
                <option value="name">Name A-Z</option>
                <option value="users">Most Users</option>
                <option value="projects">Most Projects</option>
                <option value="newest">Newest First</option>
                <option value="oldest">Oldest First</option>
              </select>
            </div>
          </div>

          {/* Results count */}
          <div className="mt-4 flex items-center justify-between">
            <span className="text-sm text-gray-500 dark:text-gray-400">
              {filteredCompanies.length} {filteredCompanies.length === 1 ? "organization" : "organizations"}
            </span>
            {(searchQuery || statusFilter !== "all") && (
              <button
                onClick={() => {
                  setSearchQuery("");
                  setStatusFilter("all");
                  setSortBy("name");
                }}
                className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 flex items-center gap-1"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path d="M6 18L18 6M6 6l12 12" />
                </svg>
                Clear filters
              </button>
            )}
          </div>
        </div>

        {/* Organizations Grid */}
        {loading ? (
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow border dark:border-gray-800 p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500 mx-auto"></div>
            <p className="mt-4 text-gray-500 dark:text-gray-400">Loading organizations...</p>
          </div>
        ) : error ? (
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow border dark:border-gray-800 p-8 text-center">
            <p className="text-red-600 dark:text-red-400">{error}</p>
          </div>
        ) : filteredCompanies.length === 0 ? (
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow border dark:border-gray-800 p-12 text-center">
            <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                <path d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
              {searchQuery || statusFilter !== "all" ? "No organizations found" : "No organizations yet"}
            </h3>
            <p className="text-gray-500 dark:text-gray-400 mb-6">
              {searchQuery || statusFilter !== "all"
                ? "Try adjusting your filters"
                : "Create your first organization to get started"}
            </p>
            {!searchQuery && statusFilter === "all" && (
              <button
                onClick={() => setShowAddCompanyModal(true)}
                className="px-5 py-2.5 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white rounded-lg font-semibold transition-all inline-flex items-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path d="M12 4v16m8-8H4" />
                </svg>
                Create First Company
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {paginatedCompanies.map((company) => (
              <div
                key={company.id}
                className="bg-white dark:bg-gray-900 rounded-xl shadow border dark:border-gray-800 overflow-hidden hover:shadow-lg transition-shadow"
              >
                {/* Card Header */}
                <div className="p-5">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      {company.logoUrl || company.logo ? (
                        <img
                          src={company.logoUrl || company.logo}
                          alt={company.name}
                          className="w-12 h-12 rounded-xl object-cover border dark:border-gray-700"
                        />
                      ) : (
                        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center">
                          <span className="text-white font-bold text-lg">
                            {company.name?.[0]?.toUpperCase() || "C"}
                          </span>
                        </div>
                      )}
                      <div>
                        <h3 className="font-semibold text-gray-900 dark:text-gray-100">{company.name}</h3>
                        <span
                          className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ${
                            company.status === 1
                              ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300"
                              : "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300"
                          }`}
                        >
                          <span className={`w-1.5 h-1.5 rounded-full ${company.status === 1 ? "bg-green-500" : "bg-red-500"}`}></span>
                          {company.status === 1 ? "Active" : "Inactive"}
                        </span>
                      </div>
                    </div>
                  </div>

                  {company.description && (
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-3 line-clamp-2">
                      {company.description}
                    </p>
                  )}
                </div>

                {/* Stats */}
                <div className="px-5 py-3 bg-gray-50 dark:bg-gray-800/50 border-t dark:border-gray-800">
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-1.5">
                      <svg className="w-4 h-4 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
                      </svg>
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        {company.member_count ?? company.users?.length ?? 0} users
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <svg className="w-4 h-4 text-purple-500" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M2 6a2 2 0 012-2h12a2 2 0 012 2v2a2 2 0 100 4v2a2 2 0 01-2 2H4a2 2 0 01-2-2v-2a2 2 0 100-4V6z" />
                      </svg>
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        {company.client_count ?? company.projects?.length ?? 0} clients
                      </span>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="px-5 py-3 border-t dark:border-gray-800 flex items-center gap-2">
                  <button
                    onClick={() => handleViewDetails(company)}
                    className="flex-1 px-3 py-2 text-sm font-medium text-emerald-600 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 rounded-lg transition-colors flex items-center justify-center gap-1"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                    View
                  </button>
                  <button
                    onClick={() => handleEditCompany(company)}
                    className="flex-1 px-3 py-2 text-sm font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors flex items-center justify-center gap-1"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                    </svg>
                    Edit
                  </button>
                  <button
                    onClick={() => handleDeleteCompany(company)}
                    className="p-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                    title="Delete Organization"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                    </svg>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {filteredCompanies.length > itemsPerPage && (
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow border dark:border-gray-800 px-6 py-4 flex flex-col gap-3 min-[650px]:flex-row min-[650px]:items-center min-[650px]:justify-between">
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-600 dark:text-gray-400">Show</label>
              <select
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="border dark:border-gray-700 rounded px-2 py-1 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
              >
                <option value="6">6</option>
                <option value="9">9</option>
                <option value="12">12</option>
                <option value="24">24</option>
              </select>
              <span className="text-sm text-gray-600 dark:text-gray-400">
                Showing {(currentPage - 1) * itemsPerPage + 1}-
                {Math.min(currentPage * itemsPerPage, filteredCompanies.length)} of {filteredCompanies.length}
              </span>
            </div>

            <div className="flex flex-wrap items-center gap-1">
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1.5 border dark:border-gray-700 rounded-lg text-sm hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed text-gray-900 dark:text-gray-100"
              >
                Previous
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter((page) => page === 1 || page === totalPages || (page >= currentPage - 1 && page <= currentPage + 1))
                .map((page, idx, arr) => {
                  if (idx > 0 && page - arr[idx - 1] > 1) {
                    return (
                      <React.Fragment key={`ellipsis-${page}`}>
                        <span className="px-2 text-gray-400 dark:text-gray-500">...</span>
                        <button
                          onClick={() => setCurrentPage(page)}
                          className={`px-3 py-1.5 border dark:border-gray-700 rounded-lg text-sm ${
                            currentPage === page
                              ? "bg-emerald-500 text-white border-emerald-500"
                              : "hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-900 dark:text-gray-100"
                          }`}
                        >
                          {page}
                        </button>
                      </React.Fragment>
                    );
                  }
                  return (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`px-3 py-1.5 border dark:border-gray-700 rounded-lg text-sm ${
                        currentPage === page
                          ? "bg-emerald-500 text-white border-emerald-500"
                          : "hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-900 dark:text-gray-100"
                      }`}
                    >
                      {page}
                    </button>
                  );
                })}
              <button
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-1.5 border dark:border-gray-700 rounded-lg text-sm hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed text-gray-900 dark:text-gray-100"
              >
                Next
              </button>
            </div>
          </div>
        )}
        </>
        )}
      </div>

      {/* Add/Edit Organization Modal */}
      <AddEditCompanyModal
        open={showAddCompanyModal || showEditCompanyModal}
        onClose={() => {
          setShowAddCompanyModal(false);
          setShowEditCompanyModal(false);
          setSelectedCompany(null);
        }}
        onSuccess={fetchCompanies}
        company={showEditCompanyModal ? selectedCompany : null}
      />

      {/* Company Detail Modal */}
      <CompanyDetailModal
        open={showDetailModal}
        onClose={() => {
          setShowDetailModal(false);
          setSelectedCompany(null);
        }}
        companyId={selectedCompany?.id}
        onUpdate={fetchCompanies}
      />
    </HubShell>
  );
};

export default CompanyManagementPage;



